// src/components/InvitePromptBanner.js
//
// Ticket 16: banner rendered inside AdminLayout when the signed-in
// user has one or more pending invites addressed to their email.
// Shows the first invite with an Accept button; additional invites
// are indicated with a "+N more" hint.

import React, { useState } from 'react';
import { Mail, CheckCircle, AlertTriangle } from 'lucide-react';
import { useSession } from '../auth/sessionContext';

const InvitePromptBanner = () => {
  const { pendingInvites, acceptPendingInvite } = useSession();
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  if (!pendingInvites || pendingInvites.length === 0) return null;
  const invite = pendingInvites[0];

  const handleAccept = async () => {
    setAccepting(true);
    setError('');
    try {
      await acceptPendingInvite(invite);
    } catch (err) {
      setError(err.message || 'Could not accept invite.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex items-center gap-3">
      <Mail size={18} className="text-blue-600 flex-shrink-0" />
      <div className="flex-1 text-sm text-slate-700">
        You have a pending invitation to join{' '}
        <strong>{invite.orgId}</strong> as <strong>{invite.role}</strong>.
        {pendingInvites.length > 1 && (
          <span className="text-slate-500 ml-2">
            (+{pendingInvites.length - 1} more)
          </span>
        )}
      </div>
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-700">
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      <button
        onClick={handleAccept}
        disabled={accepting}
        className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-1"
      >
        <CheckCircle size={14} />
        {accepting ? 'Accepting...' : 'Accept'}
      </button>
    </div>
  );
};

export default InvitePromptBanner;
